import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, Check, Loader2, Mail, Send, X } from 'lucide-react';
import { planningSauvegardeApi } from '../../lib/api';

/**
 * Réglages de la sauvegarde automatique du planning : l'établissement reçoit, par e-mail, le planning en PDF
 * à l'heure qu'il choisit. DÉSACTIVÉE par défaut ; rien ne part sans une adresse enregistrée par le titulaire.
 *
 * Écran volontairement minimal : un interrupteur, quand, quelle semaine, quel fichier (PDF, Excel ou les deux), à qui.
 * La destination (e-mail) n'a qu'un seul choix pour l'instant : elle n'est pas affichée. L'historique reste replié.
 * Tout est validé aussi côté serveur : l'écran ne fait que guider.
 */

type Reglages = {
    enabled: boolean;
    frequence: 'hebdo' | 'quotidien';
    jour_semaine: number;
    heure: string;
    portee: 'suivante' | 'en_cours';
    format: 'pdf' | 'excel' | 'pdf_excel';
    destination: 'email';
    emails: string[];
};
type Ligne = { ran_at: string; declencheur: string; statut: 'ok' | 'erreur' | 'ignore'; message?: string | null; week_start?: string | null };
type Retour = { type: 'ok' | 'info' | 'erreur'; texte: string };

const VIDE: Reglages = { enabled: false, frequence: 'hebdo', jour_semaine: 7, heure: '03:00', portee: 'suivante', format: 'pdf', destination: 'email', emails: [] };
const JOURS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const HEURES = Array.from({ length: 48 }, (_, i) => `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`);
const MAX_ADRESSES = 5;
const RE_EMAIL = /^[^\s@<>"'`,;]+@[^\s@<>"'`,;]+\.[^\s@<>"'`,;]{2,}$/;

const quand = (iso: string) => {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};
const memes = (a: Reglages, b: Reglages) => JSON.stringify(a) === JSON.stringify(b);

const champ = 'h-10 w-full rounded-[9px] border border-border bg-surface px-3 text-[12.5px] text-content focus:outline-none focus-visible:ring-2 focus-visible:ring-content/40';
const boutonPlein = 'h-9 inline-flex items-center justify-center gap-2 px-5 rounded-full bg-content text-surface text-[13px] font-semibold whitespace-nowrap hover:-translate-y-px transition-transform disabled:opacity-50 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 focus-visible:ring-offset-2';
const boutonContour = 'h-9 inline-flex items-center justify-center gap-2 px-5 rounded-full border border-border bg-surface text-[13px] font-semibold text-content whitespace-nowrap hover:border-content disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 focus-visible:ring-offset-2';

export function PlanningBackupSettings() {
    const [chargement, setChargement] = useState(true);
    const [enregistre, setEnregistre] = useState<Reglages>(VIDE);
    const [brouillon, setBrouillon] = useState<Reglages>(VIDE);
    const [derniere, setDerniere] = useState<Ligne | null>(null);
    const [saisie, setSaisie] = useState('');
    const [erreurSaisie, setErreurSaisie] = useState('');
    const [envoi, setEnvoi] = useState(false);
    const [sauvegarde, setSauvegarde] = useState(false);
    const [retour, setRetour] = useState<Retour | null>(null);
    const [historique, setHistorique] = useState<Ligne[] | null>(null);
    const montee = useRef(true);

    useEffect(() => { montee.current = true; return () => { montee.current = false; }; }, []);

    const charger = useCallback(async () => {
        try {
            const r: any = await planningSauvegardeApi.getReglages();
            if (!montee.current) return;
            const lus = { ...VIDE, ...(r?.reglages || {}) } as Reglages;
            setEnregistre(lus); setBrouillon(lus); setDerniere(r?.derniere || null);
        } catch (e: any) {
            if (montee.current) setRetour({ type: 'erreur', texte: e?.message || 'Impossible de charger les réglages.' });
        } finally {
            if (montee.current) setChargement(false);
        }
    }, []);
    useEffect(() => { charger(); }, [charger]);

    const modifie = !memes(enregistre, brouillon);
    const changer = (partiel: Partial<Reglages>) => { setRetour(null); setBrouillon((b) => ({ ...b, ...partiel })); };

    const jourChoisi = brouillon.frequence === 'quotidien' ? 'tous' : String(brouillon.jour_semaine);
    const choisirJour = (v: string) => (v === 'tous' ? changer({ frequence: 'quotidien' }) : changer({ frequence: 'hebdo', jour_semaine: Number(v) }));

    const ajouterAdresse = () => {
        const a = saisie.trim().toLowerCase();
        if (!a) return;
        if (!RE_EMAIL.test(a) || a.length > 254) { setErreurSaisie('Cette adresse e-mail ne semble pas valide.'); return; }
        if (brouillon.emails.includes(a)) { setErreurSaisie('Cette adresse est déjà dans la liste.'); return; }
        if (brouillon.emails.length >= MAX_ADRESSES) { setErreurSaisie(`${MAX_ADRESSES} adresses au maximum.`); return; }
        setErreurSaisie(''); setSaisie('');
        changer({ emails: [...brouillon.emails, a] });
    };
    const retirerAdresse = (a: string) => changer({ emails: brouillon.emails.filter((x) => x !== a) });

    const enregistrer = async () => {
        setSauvegarde(true); setRetour(null);
        try {
            await planningSauvegardeApi.saveReglages(brouillon);
            setEnregistre(brouillon);
            setRetour({ type: 'ok', texte: brouillon.enabled ? 'Enregistré. La sauvegarde automatique est activée.' : 'Enregistré. La sauvegarde automatique est désactivée.' });
        } catch (e: any) {
            setRetour({ type: 'erreur', texte: e?.message || "L'enregistrement a échoué." });
        } finally { setSauvegarde(false); }
    };

    const envoyerMaintenant = async () => {
        setEnvoi(true); setRetour(null);
        try {
            const r: any = await planningSauvegardeApi.executer();
            setRetour(r?.statut === 'ok' ? { type: 'ok', texte: r.message || 'Sauvegarde envoyée.' }
                : r?.statut === 'ignore' ? { type: 'info', texte: r.message }
                : { type: 'erreur', texte: r?.message || "La sauvegarde n'a pas pu être envoyée." });
            charger();
            if (historique) ouvrirHistorique();
        } catch (e: any) {
            setRetour({ type: 'erreur', texte: e?.message || "La sauvegarde n'a pas pu être envoyée." });
        } finally { setEnvoi(false); }
    };

    const ouvrirHistorique = async () => {
        try { const r: any = await planningSauvegardeApi.getHistorique(); if (montee.current) setHistorique(r?.historique || []); }
        catch { if (montee.current) setHistorique([]); }
    };

    const peutEnvoyer = useMemo(() => !modifie && enregistre.emails.length > 0 && !envoi, [modifie, enregistre.emails.length, envoi]);

    if (chargement) return <div className="flex justify-center py-12" role="status" aria-label="Chargement"><Loader2 className="animate-spin text-content-muted" size={22} /></div>;

    return (
        <div className="flex flex-col gap-5 max-w-[560px]">
            {/* 1. L'interrupteur : désactivé par défaut */}
            <div className="flex items-start gap-3 rounded-[10px] border border-border bg-surface-soft px-[13px] py-3">
                <button
                    type="button" role="switch" aria-checked={brouillon.enabled} aria-labelledby="sauv-titre" aria-describedby="sauv-aide"
                    onClick={() => changer({ enabled: !brouillon.enabled })}
                    className={`relative mt-0.5 h-[22px] w-[40px] shrink-0 rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 focus-visible:ring-offset-2 ${brouillon.enabled ? 'bg-content border-content' : 'bg-surface border-border'}`}
                >
                    <span className={`absolute top-[2px] h-4 w-4 rounded-full transition-all ${brouillon.enabled ? 'left-[20px] bg-surface' : 'left-[2px] bg-content-muted'}`} />
                </button>
                <div className="min-w-0">
                    <div id="sauv-titre" className="text-[12.5px] font-semibold text-content">Recevoir le planning par e-mail</div>
                    <div id="sauv-aide" className="mt-0.5 text-[11.5px] leading-snug text-content-muted text-pretty">
                        {brouillon.enabled ? 'Une copie PDF du planning est envoyée automatiquement.' : "Désactivé : rien n'est envoyé."}
                    </div>
                </div>
            </div>

            {/* 2. Quand, quelle semaine */}
            <div className={`flex flex-col gap-4 ${brouillon.enabled ? '' : 'opacity-60'}`}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="sauv-jour" className="block text-xs font-semibold mb-1.5 text-content">Quel jour</label>
                        <select id="sauv-jour" className={champ} value={jourChoisi} onChange={(e) => choisirJour(e.target.value)}>
                            <option value="tous">Tous les jours</option>
                            {JOURS.map((j, i) => <option key={j} value={String(i + 1)}>{j}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="sauv-heure" className="block text-xs font-semibold mb-1.5 text-content">À quelle heure</label>
                        <select id="sauv-heure" className={champ} value={brouillon.heure} onChange={(e) => changer({ heure: e.target.value })}>
                            {HEURES.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                    </div>
                </div>

                <fieldset>
                    <legend className="block text-xs font-semibold mb-1.5 text-content">Quelle semaine</legend>
                    <div className="flex gap-[7px]">
                        {([['suivante', 'La semaine à venir'], ['en_cours', 'La semaine en cours']] as const).map(([valeur, libelle]) => {
                            const actif = brouillon.portee === valeur;
                            return (
                                <button key={valeur} type="button" aria-pressed={actif} onClick={() => changer({ portee: valeur })}
                                    className={`flex-1 h-10 flex items-center justify-center rounded-[9px] border text-[12.5px] font-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 ${actif ? 'bg-content border-content text-surface' : 'bg-surface border-border text-content hover:border-content'}`}>
                                    {libelle}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>

                <fieldset>
                    <legend className="block text-xs font-semibold mb-1.5 text-content">Quel fichier</legend>
                    <div className="flex gap-[7px]">
                        {([['pdf', 'PDF'], ['excel', 'Excel'], ['pdf_excel', 'Les deux']] as const).map(([valeur, libelle]) => {
                            const actif = brouillon.format === valeur;
                            return (
                                <button key={valeur} type="button" aria-pressed={actif} onClick={() => changer({ format: valeur })}
                                    className={`flex-1 h-10 flex items-center justify-center rounded-[9px] border text-[12.5px] font-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 ${actif ? 'bg-content border-content text-surface' : 'bg-surface border-border text-content hover:border-content'}`}>
                                    {libelle}
                                </button>
                            );
                        })}
                    </div>
                </fieldset>

                {/* 3. À qui */}
                <div>
                    <label htmlFor="sauv-adresse" className="block text-xs font-semibold mb-1.5 text-content">À quelles adresses e-mail</label>
                    <div className="flex gap-2">
                        <input
                            id="sauv-adresse" type="email" inputMode="email" autoComplete="off" className={champ} value={saisie}
                            placeholder="prenom@exemple.fr" aria-invalid={!!erreurSaisie} aria-describedby={erreurSaisie ? 'sauv-adresse-erreur' : undefined}
                            onChange={(e) => { setSaisie(e.target.value); if (erreurSaisie) setErreurSaisie(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouterAdresse(); } }}
                        />
                        <button type="button" onClick={ajouterAdresse} disabled={!saisie.trim()} className={boutonContour}>Ajouter</button>
                    </div>
                    {erreurSaisie && <p id="sauv-adresse-erreur" role="alert" className="mt-1.5 text-[11.5px] text-red-600 dark:text-red-400">{erreurSaisie}</p>}
                    {brouillon.emails.length > 0 && (
                        <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Adresses enregistrées">
                            {brouillon.emails.map((a) => (
                                <li key={a} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-surface-soft pl-3 pr-1 h-8 text-[12px] text-content">
                                    <Mail size={12} aria-hidden="true" className="text-content-muted" />
                                    <span className="max-w-[240px] truncate">{a}</span>
                                    <button type="button" onClick={() => retirerAdresse(a)} aria-label={`Retirer ${a}`}
                                        className="h-6 w-6 inline-flex items-center justify-center rounded-full hover:bg-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40">
                                        <X size={12} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                    <p className="mt-2 text-[11.5px] leading-snug text-content-muted text-pretty">
                        Le planning contient les noms et horaires de votre équipe : n'indiquez que des adresses de confiance ({MAX_ADRESSES} au maximum).
                    </p>
                </div>
            </div>

            {/* 4. Les deux gestes */}
            <div className="flex flex-wrap items-center gap-2">
                <button type="button" onClick={enregistrer} disabled={!modifie || sauvegarde} className={boutonPlein}>
                    {sauvegarde ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Enregistrer
                </button>
                <button type="button" onClick={envoyerMaintenant} disabled={!peutEnvoyer} className={boutonContour}
                    aria-describedby="sauv-maintenant-aide">
                    {envoi ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Envoyer maintenant
                </button>
            </div>
            <p id="sauv-maintenant-aide" className="-mt-3 text-[11.5px] leading-snug text-content-muted text-pretty">
                {modifie ? "Enregistrez d'abord vos changements pour pouvoir envoyer une sauvegarde."
                    : enregistre.emails.length === 0 ? 'Ajoutez une adresse et enregistrez pour pouvoir envoyer une sauvegarde.'
                    : 'Envoie tout de suite le planning aux adresses enregistrées, pour vérifier que tout fonctionne.'}
            </p>

            {/* Retour d'action : annoncé aux lecteurs d'écran, jamais par la couleur seule */}
            <div aria-live="polite" role="status" className="min-h-[1px]">
                {retour && (
                    <div className={`flex items-start gap-2 rounded-[10px] border px-3 py-2.5 text-[12.5px] ${retour.type === 'erreur' ? 'border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300 dark:border-red-900' : 'border-border bg-surface-soft text-content'}`}>
                        {retour.type === 'erreur' ? <AlertCircle size={15} className="mt-px shrink-0" aria-hidden="true" /> : <Check size={15} className="mt-px shrink-0" aria-hidden="true" />}
                        <span><strong className="font-semibold">{retour.type === 'erreur' ? 'Erreur : ' : retour.type === 'ok' ? 'Fait : ' : 'À savoir : '}</strong>{retour.texte}</span>
                    </div>
                )}
            </div>

            {/* 5. Dernière sauvegarde et historique, repliés */}
            <div className="border-t border-border pt-3">
                <p className="text-[12px] text-content-muted">
                    {derniere
                        ? <>Dernière sauvegarde : <strong className="text-content font-semibold">{derniere.statut === 'ok' ? 'envoyée' : derniere.statut === 'ignore' ? 'ignorée' : 'en erreur'}</strong> le {quand(derniere.ran_at)}.</>
                        : "Aucune sauvegarde n'a encore été envoyée."}
                </p>
                <details className="mt-2" onToggle={(e) => { if ((e.currentTarget as HTMLDetailsElement).open && historique === null) ouvrirHistorique(); }}>
                    <summary className="cursor-pointer text-[12px] font-semibold text-content hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-content/40 rounded">Voir l'historique</summary>
                    <div className="mt-2">
                        {historique === null ? <Loader2 size={14} className="animate-spin text-content-muted" aria-label="Chargement" />
                            : historique.length === 0 ? <p className="text-[12px] text-content-muted">Rien pour le moment.</p>
                            : (
                                <ul className="flex flex-col divide-y divide-border border border-border rounded-[10px] overflow-hidden">
                                    {historique.map((l, i) => (
                                        <li key={`${l.ran_at}-${i}`} className="flex items-start gap-2 px-3 py-2 text-[12px]">
                                            <span className="mt-px shrink-0" aria-hidden="true">{l.statut === 'ok' ? <Check size={13} /> : <AlertCircle size={13} />}</span>
                                            <span className="min-w-0">
                                                <span className="font-semibold text-content">{quand(l.ran_at)}</span>
                                                <span className="text-content-muted"> · {l.declencheur === 'manuel' ? 'à la main' : 'automatique'} · {l.statut === 'ok' ? 'envoyée' : l.statut === 'ignore' ? 'ignorée' : 'en erreur'}</span>
                                                {l.message && <span className="block text-content-muted text-pretty">{l.message}</span>}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                    </div>
                </details>
            </div>
        </div>
    );
}
